import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { ACTIVITY_TYPE, DRAWER_CONTENTS, ROLE, ROUTES, TABS_CONSTANTS, MAX_LENGTH_VARIABLES } from '../../const';
import { I18n } from 'react-redux-i18n-lite';
import * as messageAlert from '../../actions/messageAlertAction';
import BookButtonsContainer from './BookButtonsContainer';
import Truncate from 'react-truncate';
import TagsList from './tags/TagsList';
import * as customTranslate from '../../services/translate/customTranslateService';
import * as bookTagsListService from '../../services/domain/bookTagsListService';
import * as drawerActions from '../../actions/drawerActions';
import * as bookActions from '../../actions/bookActions';
import * as contentActions from '../../actions/contentActions';
import * as bookApi from '../../services/api/bookApi';
import { prettyName } from '../../utils/stringUtils';
import * as optionItemsFactory from '../../factory/optionItemsFactory';
import * as gradebookActions from '../../actions/classroom/gradebookActions';
import * as googleClassroomActions from '../../actions/googleClassroomActions';
import routerHistory from './../../routerHistory';
import { isElectronApp } from '../../services/crossPlatform/crossPlatformService';
import MoreOptions from '../common/sheets/MoreOptions';
import EditNameSheet from '../common/sheets/EditNameSheet';
import { isMobileViewport, MOBILE_MEDIA_QUERY } from '../../utils/mobileUtils';
import Media from 'react-media';
import CustomTooltip from '../common/CustomTooltip';
import * as analyticsApi from '../../services/api/analyticsApi';
import { ANALYTICS_CONTEXT, ANALYTICS_EVENT } from '../../types/analytics';

const parseLinkedClassrooms = response => {
  const data = response && response.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
};

class BookHeader extends Component {
  state = {
    title: '',
    edit: false,
    isEditSheetOpen: false,
    isDescriptionTruncated: false,
    linkedClassrooms: []
  };

  inputTitle = React.createRef();

  componentDidMount() {
    this.fetchLinkedClassrooms();
  }

  componentDidUpdate(prevProps) {
    const { book, classroom, activityLinkChanged } = this.props;
    const activityId = book && book.activityId;
    const sectionId = classroom && classroom.sectionId;

    if (
      (prevProps.book && prevProps.book.activityId) !== activityId ||
      (prevProps.classroom && prevProps.classroom.sectionId) !== sectionId
    ) {
      this.fetchLinkedClassrooms();
      return;
    }

    if (prevProps.activityLinkChanged !== activityLinkChanged && activityLinkChanged) {
      const changedActivityIds =
        activityLinkChanged.activityIds ||
        (activityLinkChanged.activityId ? [activityLinkChanged.activityId] : []);

      if (activityId && changedActivityIds.includes(activityId)) {
        this.fetchLinkedClassrooms();
      }
    }
  }

  fetchLinkedClassrooms = () => {
    const { book, classroom, role } = this.props;
    const activityId = book && book.activityId;
    const sectionId = classroom && classroom.sectionId;

    if (role === ROLE.STUDENT || !activityId || !sectionId) {
      this.setState({ linkedClassrooms: [] });
      return;
    }

    bookApi
      .getLinkedClassroomsForActivity(activityId, sectionId)
      .then(res => {
        this.setState({ linkedClassrooms: parseLinkedClassrooms(res) });
      })
      .catch(() => {
        this.setState({ linkedClassrooms: [] });
      });
  };

  getBackgroundImg = () => ({ backgroundImage: `url(${this.props.imageSrc})` });

  createTask = async () => {
    const { book, drawerActions, classroom } = this.props;
    await drawerActions.getContentAndOpen(DRAWER_CONTENTS.CREATE_ASSIGNMENTS_FROM_BOOK, classroom.id, book.id);
  };

  handleDownloadButton = () => {
    const { book, contentActions, classroom, language } = this.props;
    const title = customTranslate.translateObject(book.name, language);
    this.setState({ saveBookAlreadyCalled: true }, () => contentActions.saveBook(classroom.id, book, title));
  };

  handleReDownloadButton = () => {
    const { book, bookActions } = this.props;
    bookActions.setReDownloadBookState(book);
  };

  enableEditTitle = () => {
    const { book, language } = this.props;

    if (this.state.title === '') {
      const newBookName = book.name;
      const title = customTranslate.translateObject(newBookName, language);

      if (title) {
        this.setState({ title: title });
      }
    }

    this.setState({ edit: true });
  };

  updateTitle = e => this.setState({ title: e.target.value });

  renderTitleTextEditable = () => {
    const { title } = this.state;
    const { isAppOnline } = this.props;
    const size = title ? title.length : 5;

    return (
      <input
        ref={this.inputTitle}
        onChange={this.updateTitle}
        onBlur={this.handleBlur}
        onKeyPress={this.handleEnter}
        size={size}
        value={title}
        disabled={!isAppOnline}
      />
    );
  };

  renderTitleText = () => {
    const { isComplete, authoring } = this.props;
    const { edit } = this.state;

    if (isComplete && authoring && edit) return this.renderTitleTextEditable();
    return this.renderTitleTextReadable();
  };

  getBookTitle() {
    const { book, language } = this.props;

    return book && book.name ? customTranslate.translateObject(book.name, language) : '';
  }

  renderTitleTextReadable = () => {
    const classroomName = prettyName(this.getBookTitle(), 80);
    // Mobile: the whole name wraps (clamped in CSS); Truncate measures its container, which shrinks with the text
    return (
      <Media query={MOBILE_MEDIA_QUERY}>
        {isMobile =>
          isMobile ? (
            <span className="book-title-text">{this.getBookTitle()}</span>
          ) : (
            <Truncate className="truncate-class" lines={1} ellipsis={<span>...</span>}>
              {classroomName}
            </Truncate>
          )
        }
      </Media>
    );
  };

  handleDescriptionTruncate = (isTruncated) => {
    if (this.state.isDescriptionTruncated !== isTruncated) {
      this.setState({ isDescriptionTruncated: isTruncated });
    }
  };

  renderSubtitleText = text => {
    const isTruncated = this.state.isDescriptionTruncated;
    
    return (
      <div>
        {isTruncated ? (
          <CustomTooltip title={text} classes="" placement="bottom">
            <Truncate 
              className="truncate-class" 
              lines={3} 
              ellipsis={<span>...</span>} 
              onTruncate={this.handleDescriptionTruncate}
            >
              {text}
            </Truncate>
          </CustomTooltip>
        ) : (
          <Truncate 
            className="truncate-class" 
            lines={3} 
            ellipsis={<span>...</span>} 
            onTruncate={this.handleDescriptionTruncate}
          >
            {text}
          </Truncate>
        )}
      </div>
    );
  };

  handleBlur = e => {
    const { isAppOnline, book, messageAlert } = this.props;
    const titleMaxLength = MAX_LENGTH_VARIABLES.BOOK_NAME;

    if (book && book.name && typeof book.name === 'object') {
      const title = e.target.value;

      if (title && title.length > titleMaxLength) {
        messageAlert.error(
          I18n.t('error.valueLength', { variable: I18n.t('common.unitTitle'), length: titleMaxLength })
        );
        return;
      } else {
        if (title === '') {
          this.setState({ title: this.getBookTitle() });
        } else {
          if (isAppOnline) {
            this.updateBook();
            messageAlert.success(`"${title}" ${I18n.t('common.bookSaved')}`);
          }
        }
      }
    }
  };

  openEditSheet = () => {
    if (this.props.isAppOnline) {
      this.setState({ isEditSheetOpen: true });
    }
  };

  saveTitleFromSheet = title => {
    const { messageAlert } = this.props;
    this.setState({ title, isEditSheetOpen: false }, () => {
      this.updateBook();
      messageAlert.success(`"${title}" ${I18n.t('common.bookSaved')}`);
    });
  };

  updateBook = () => {
    const { title } = this.state;
    const { bookActions, classroom, book } = this.props;
    bookActions.updateBook(classroom.id, book.id, title);
  };

  handleEnter = e => {
    const { isAppOnline } = this.props;
    if (isAppOnline && e.key === 'Enter') {
      e.preventDefault();
      this.inputTitle.current.blur();
    }
  };

  editAssign = () => {
    const { isAppOnline, drawerActions, book, bookActions } = this.props;
    if (isAppOnline) {
      bookActions.setSelectedResource(Object.assign({ isBook: true }, book));
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.ASSIGN_STUDENTS);
    }
  };

  selectCourseToShareInGoogle = () => {
    const { drawerActions, book, googleClassroomActions } = this.props;
    googleClassroomActions.setContent(book, ACTIVITY_TYPE.BOOK);
    drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.SELECT_COURSE_GOOGLE);
  };

  createTask = async () => {
    const { book, drawerActions, classroom } = this.props;
    await drawerActions.getContentAndOpen(DRAWER_CONTENTS.CREATE_ASSIGNMENTS_FROM_BOOK, classroom.id, book.id);
  };

  openNewUnitDrawer = () => {
    this.props.drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.UNIT_CREATE);
  };

  getOptionItems = () => {
    const { menuRoutes, allowShareInGoogleClassroom, isAppOnline, unitId, authoring } = this.props;
    const options = [];
    const isMobile = isMobileViewport();

    if (this.showOptionsInstructor()) {
      // On mobile there is no tap on the title to rename the book: it is the first option of the sheet
      if (isMobile && authoring) {
        options.push(optionItemsFactory.getEditItem(this.openEditSheet, isAppOnline));
      }
      if (menuRoutes.includes(TABS_CONSTANTS.ASSIGNMENTS)) {
        options.push(optionItemsFactory.getCreateTaskItem(this.createTask, isAppOnline));
        options.push(optionItemsFactory.seeTaksItem(this.goToTasks, isAppOnline));
      }
      if (menuRoutes.includes(TABS_CONSTANTS.GRADEBOOK)) {
        options.push(optionItemsFactory.seeGradesItem(this.goToGradebook, isAppOnline));
      }
      if(!unitId) {
        options.push(optionItemsFactory.getAssignItem(this.editAssign, isAppOnline));
        if (allowShareInGoogleClassroom && !isElectronApp()) {
          options.push(optionItemsFactory.shareInGoogleClassroom(this.selectCourseToShareInGoogle, isAppOnline));
        }
        if (!this.props.hideCreateUnitButton) {
          options.push(optionItemsFactory.getNewUnitDrawer(this.openNewUnitDrawer, isAppOnline));
        }
      }
    }

    if (this.showOptionsStudent()) {
      // Mobile sheet shortcuts carry a help line; the desktop dropdown ignores it
      const describe = (item, key) => (isMobile ? { ...item, description: I18n.t(`mobileSheets.${key}`) } : item);
      if (menuRoutes.includes(TABS_CONSTANTS.ASSIGNMENTS)) {
        const tasks = optionItemsFactory.seeTaksItem(this.goToTasks, isAppOnline);
        options.push(describe(tasks, 'tasksHelp'));
      }
      if (menuRoutes.includes(TABS_CONSTANTS.STUDENT_ANNOTATIONS)) {
        const annotations = optionItemsFactory.seeAnnotationsItem(this.goToAnnotations, isAppOnline);
        // Mobile sheet lists My notes first
        if (isMobile) {
          options.unshift(describe(annotations, 'annotationsHelp'));
        } else {
          options.push(annotations);
        }
      }
      if (isMobile && menuRoutes.includes(TABS_CONSTANTS.DISCUSSIONS)) {
        const discussions = optionItemsFactory.seeDiscussionsItem(this.goToDiscussions, isAppOnline);
        options.push(describe(discussions, 'discussionsHelp'));
      }
    }

    return options;
  };

  goToTasks = () => {
    const { classroom } = this.props;
    routerHistory.push(ROUTES.CLASSROOM_TASKS.replace('_classId_', classroom.id));
  };

  goToAnnotations = () => {
    const { classroom } = this.props;
    analyticsApi.trackAnalyticsEvent({
      event: ANALYTICS_EVENT.VIEW_MY_NOTES,
      classId: classroom.id,
      // On mobile the option comes from the book options sheet
      context: isMobileViewport() ? ANALYTICS_CONTEXT.MY_NOTES_SHEET : ANALYTICS_CONTEXT.MY_NOTES,
      status: 'success'
    });
    routerHistory.push(ROUTES.STUDENT_ANNOTATIONS.replace('_classId_', classroom.id));
  };

  goToDiscussions = () => {
    const { classroom } = this.props;
    routerHistory.push(`${ROUTES.CLASSROOM}/${classroom.id}/discussions`);
  };

  goToGradebook = () => {
    const { classroom, book, gradebookActions } = this.props;
    gradebookActions.setData(classroom.id, book);
    routerHistory.push(ROUTES.GRADEBOOK.replace('_classId_', classroom.id));
  };

  showOptionsInstructor = () => {
    const { role, isPreview, isComplete, isAppOnline } = this.props;
    return role === ROLE.INSTRUCTOR && !isPreview && isComplete && isAppOnline;
  };

  showOptionsStudent = () => {
    const { role, isAppOnline, menuRoutes } = this.props;
    return (
      role === ROLE.STUDENT &&
      isAppOnline &&
      (menuRoutes.includes(TABS_CONSTANTS.ASSIGNMENTS) ||
        menuRoutes.includes(TABS_CONSTANTS.STUDENT_ANNOTATIONS) ||
        (isMobileViewport() && menuRoutes.includes(TABS_CONSTANTS.DISCUSSIONS)))
    );
  };

  shouldComponentUpdate(nextProps, nextState) {
    const relevantPropsChanged = 
      this.props.book.id !== nextProps.book.id ||
      this.props.book.name !== nextProps.book.name ||
      this.props.book.description !== nextProps.book.description ||
      this.props.language !== nextProps.language ||
      this.props.isComplete !== nextProps.isComplete ||
      this.props.authoring !== nextProps.authoring ||
      this.props.isAppOnline !== nextProps.isAppOnline ||
      this.props.hideCreateUnitButton !== nextProps.hideCreateUnitButton ||
      this.props.menuRoutes !== nextProps.menuRoutes ||
      this.props.role !== nextProps.role;
      
    const relevantStateChanged = 
      this.state.edit !== nextState.edit ||
      this.state.title !== nextState.title ||
      this.state.isEditSheetOpen !== nextState.isEditSheetOpen ||
      this.state.isDescriptionTruncated !== nextState.isDescriptionTruncated;
      
    return relevantPropsChanged || relevantStateChanged;
  }

  render() {
    const { book, isPreview, isComplete, unitId } = this.props;
    const { linkedClassrooms } = this.state;

    const bookBackgroundImg = this.getBackgroundImg();
    const tagsAndMetadata = bookTagsListService.getTagsAndMetadataList(book.tags, book.metadata);
    const isSynchronized = linkedClassrooms.length > 1;

    return (
      <>
        <div id={`book-header-${book.id}`} className="book-landscape" style={bookBackgroundImg}>
          <div className="cover-gradient">
            <div className="header-tag">
              <TagsList tags={tagsAndMetadata} />
              <span className="options-wrapper">
                {(this.showOptionsInstructor() || this.showOptionsStudent()) && (
                  <MoreOptions
                    id={`book-options-${book.id}`}
                    color="white"
                    dropdownSide="left"
                    options={this.getOptionItems()}
                    sheetTitle={this.getBookTitle()}
                    sheetOverline={[I18n.t('mobileSheets.book'), ...tagsAndMetadata.map(tag => `#${tag}`)].join(' ')}
                    sheetMedia={<span className="sheet-media-image" style={bookBackgroundImg} />}
                  />
                )}
              </span>
            </div>
            <div className="title-wrapper">
              <h2 className="book-title">
                <div className="book-title-input" onClick={this.enableEditTitle}>
                  {this.renderTitleText()}
                </div>
              </h2>
            </div>
            {book.description && <div className="book-subtitle">{this.renderSubtitleText(book.description)}</div>}
            <BookButtonsContainer
              book={book}
              unitId={unitId}
              handleDownloadButton={this.handleDownloadButton}
              handleReDownloadButton={this.handleReDownloadButton}
              isPreview={isPreview}
              isComplete={isComplete}
              withMargin={!book.description && isSynchronized}
            />
          </div>
        </div>
        {this.showOptionsInstructor() && (
          <EditNameSheet
            open={this.state.isEditSheetOpen}
            title={I18n.t('mobileSheets.editBook')}
            initialValue={this.getBookTitle()}
            maxLength={MAX_LENGTH_VARIABLES.BOOK_NAME}
            onSave={this.saveTitleFromSheet}
            onClose={() => this.setState({ isEditSheetOpen: false })}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = state => ({
  role: state.classrooms.selectedClassroom.classroom.role,
  classroom: state.classrooms.selectedClassroom.classroom,
  language: state.account.user.language,
  isAppOnline: state.connection.status.online,
  authoring: state.account.configuration.authoring,
  allowShareInGoogleClassroom: state.account.configuration.allowShareInGoogleClassroom,
  activityLinkChanged: state.book.activityLinkChanged,
  menuRoutes: state.account.configuration.classroomTabs,
  hideCreateUnitButton: state.account.configuration.hideCreateUnitButton,
});

const mapDispatchToProps = dispatch => ({
  bookActions: bindActionCreators(bookActions, dispatch),
  contentActions: bindActionCreators(contentActions, dispatch),
  drawerActions: bindActionCreators(drawerActions, dispatch),
  gradebookActions: bindActionCreators(gradebookActions, dispatch),
  messageAlert: bindActionCreators(messageAlert, dispatch),
  googleClassroomActions: bindActionCreators(googleClassroomActions, dispatch),
  dispatch
});

BookHeader.propTypes = {
  book: PropTypes.object.isRequired,
  classroom: PropTypes.object,
  isComplete: PropTypes.bool,
  isPreview: PropTypes.bool,
  language: PropTypes.any,
  classroomId: PropTypes.any,
  bookActions: PropTypes.any,
  unitId: PropTypes.any,
  authoring: PropTypes.any,
  isAppOnline: PropTypes.any,
  drawerActions: PropTypes.any,
  imageSrc: PropTypes.any,
  contentActions: PropTypes.any,
  role: PropTypes.string,
  activityLinkChanged: PropTypes.object,
  menuRoutes: PropTypes.array,
  messageAlert: PropTypes.object,
  gradebookActions: PropTypes.any
};

export default connect(mapStateToProps, mapDispatchToProps)(BookHeader);
